import { useEffect, useRef, useState } from 'react';
import { useUser } from '../app/session';
import { api, loadToken } from '../lib/api';
import { BackLink, Header, Loader, Screen, TabBar } from '../components/Chrome';
import '../styles/notifications.css';

// Kept local to web: the backend owns the shared/schema contract.
export interface NotificationSettings {
  reminders: boolean;
  daily: boolean;
  weekly: boolean;
  leagues: boolean;
  pausedUntil: string | null;
}
type Category = Exclude<keyof NotificationSettings, 'pausedUntil'>;
const categories: { key: Category; title: string; detail: string }[] = [
  { key: 'reminders', title: 'Напоминания', detail: 'Если давно не было записей' },
  { key: 'daily', title: 'Итоги дня', detail: 'Питание и тренировки за день' },
  { key: 'weekly', title: 'Итоги недели', detail: 'Ваш прогресс за прошлую неделю' },
  { key: 'leagues', title: 'Лиги', detail: 'Изменения вашей лиги' },
];
function settingsResponse(value: NotificationSettings): NotificationSettings {
  if (!value || categories.some(({ key }) => typeof value[key] !== 'boolean') ||
    !(value.pausedUntil === null || typeof value.pausedUntil === 'string' && Number.isFinite(Date.parse(value.pausedUntil)))) {
    throw new Error('Не удалось прочитать настройки. Повторите загрузку.');
  }
  return value;
}
export function NotificationsScreen() {
  const user = useUser();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [message, setMessage] = useState('');
  const lock = useRef(false);
  const generation = useRef(0);
  const token = loadToken();
  const alive = useRef(false);
  const run = async (action: () => Promise<NotificationSettings>, success = '') => {
    if (lock.current) return;
    lock.current = true;
    const current = generation.current;
    const auth = loadToken();
    setBusy(true); setError(null); setMessage('');
    const valid = () => alive.current && current === generation.current && loadToken() === auth;
    try {
      const next = settingsResponse(await action());
      if (valid()) { setSettings(next); setMessage(success); }
    } catch (err) {
      if (valid()) setError(err instanceof Error ? err.message : 'Не удалось сохранить настройки');
    } finally {
      if (valid()) { lock.current = false; setBusy(false); }
    }
  };
  const load = () => run(() => api.get<NotificationSettings>('/api/me/notifications'));
  useEffect(() => {
    alive.current = true; generation.current++; lock.current = false;
    setSettings(null);
    void load();
    return () => { alive.current = false; generation.current++; };
  }, [user.id, token]);
  const change = (patch: Partial<Pick<NotificationSettings, Category>>) =>
    run(() => api.patch<NotificationSettings>('/api/me/notifications', patch), 'Настройки сохранены');
  let pauseLabel: string | null = null;
  if (settings?.pausedUntil && Date.parse(settings.pausedUntil) > Date.now()) {
    try { pauseLabel = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: user.timezone }).format(new Date(settings.pausedUntil)); }
    catch { pauseLabel = settings.pausedUntil; }
  }
  return <Screen tabs>
    <Header />
    <BackLink to="/profile" label="Профиль" />
    <h1>Уведомления</h1>
    <p className="nt notifications-intro">Сообщения от бота в Telegram. Выберите, что получать.</p>
    {!settings && busy ? <Loader /> : null}
    {settings ? <>
      <section className="card notification-categories" aria-label="Категории уведомлений" aria-busy={busy}>
        {categories.map(({key, title, detail}) => <div className="notification-row" key={key}>
          <span><b>{title}</b><small>{detail}</small></span>
          <button className="notification-switch" type="button" role="switch" aria-label={title} aria-checked={settings[key]} disabled={busy} onClick={() => void change({[key]: !settings[key]})}><span /></button>
        </div>)}
      </section>
      <section className="card notification-pause">
        <h2>Перерыв от сообщений</h2>
        <p className="nt">Пауза сохранит ваш выбор категорий.</p>
        {pauseLabel ? <p className="notification-paused">На паузе до {pauseLabel}</p> : null}
        <button className="b wide" type="button" disabled={busy} onClick={() => void run(() => api.post<NotificationSettings>('/api/me/notifications/pause', {days: 14}), 'Пауза сохранена')}>Пауза на 14 дней</button>
        <button className="b ghost wide" type="button" disabled={busy || !categories.some(({key}) => settings[key])} onClick={() => void change({reminders:false, daily:false, weekly:false, leagues:false})}>Выключить всё</button>
      </section>
    </> : null}
    {error ? <div className="notification-error"><p className="err" role="alert">{error}</p><button className="b wide" type="button" disabled={busy} onClick={() => void load()}>Повторить загрузку</button></div> : null}
    <p className="nt notification-status" role="status" aria-live="polite">{busy && settings ? 'Сохраняем…' : message}</p>
    <TabBar />
  </Screen>;
}
